import { Component, OnInit } from '@angular/core';
import { TrendReportService } from '../services/trend-report.service';
import { ActivatedRoute } from '@angular/router';
import { LocationInformationService } from '../services/location-information.service';
import { FormGroup, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { TrendService } from '../services/trend.service';
import { LoadingService } from '../services/loading.service';
import { of, Observable } from 'rxjs';
import { MatFormField, MatSelect, MatOption } from '@angular/material/select';
import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { DateSelectionComponent } from '../date-selection/date-selection.component';

import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  CanvasRenderer
]);

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ChartDataSource {
  seriesname: string;
  data: { label: string; value: number }[];
}

@Component({
  selector: 'app-site-trend-chart',
  templateUrl: './site-trend-chart.component.html',
  styleUrls: ['./site-trend-chart.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    MatButton,
    DateSelectionComponent,
    NgIf,
    AsyncPipe,
    NgxEchartsDirective
  ],
  providers: [provideEchartsCore({ echarts })]
})
export class SiteTrendChartComponent implements OnInit {
  public plant: string;
  public groupBy: string;

  public dataSource: any;
  public chartOption: any;

  public trendChartData: any;
  public shiftDateLabels: string[] = [];
  public trendChartDataSource: ChartDataSource[] = [];

  public fromDateString: string;
  public toDateString: string;

  fg_hdr: FormGroup;
  manualInput = false;

  public lineArr: string[] = [];
  public linesSelected: string[] = [];

  public lineSelectForm = new FormGroup({
    lines: new FormControl<string[]>([])
  });

  public lineListElements$: Observable<string[]> = of([]);
  public linesSelectedArr: { lines: string[] } = { lines: [] };
  public maxVal = 1;

  showChart = false;
  currentShift = 'True';

  private readonly severityOrder = ['major', 'minor', 'critical'];

  private readonly seriesPalette = [
    '#5b6ee1',
    '#22c7d6',
    '#ff6b6b',
    '#ffb020',
    '#67c587',
    '#b39ddb',
    '#6ec6ff',
    '#7986cb',
    '#26c6da',
    '#ef5350',
    '#fbc02d',
    '#81c784',
    '#8e24aa',
    '#00acc1',
    '#7cb342',
    '#3949ab',
    '#ffa726',
    '#26a69a'
  ];

  private seriesColorMap: Record<string, string> = {};

  constructor(
    private locationInformationService: LocationInformationService,
    private trendReportService: TrendReportService,
    private route: ActivatedRoute,
    private trendService: TrendService,
    private loadingService: LoadingService,
    private formBuilder: FormBuilder
  ) {
    this.plant = this.route.snapshot.paramMap.get('plant')!;
    this.groupBy = 'line, day';

    this.locationInformationService.changeLocation(true);
    this.locationInformationService.changePlant(this.plant);
    this.locationInformationService.changePlantView(true);

    this.dataSource = [];
    this.fg_hdr = this.formBuilder.group({});
  }

  ngOnInit(): void {
    setTimeout(() => {
      if (this.fromDateString && this.toDateString) {
        this.getSeverityTrendData();
      }
    });

    this.lineSelectForm.valueChanges.subscribe(val => {
      this.linesSelectedArr = { lines: val.lines ?? [] };
    });

    this.buildEmptyChartOption();

    this.trendService.getLines(this.plant).subscribe(res => {
      this.lineArr = res.Body.map((l: any) => l.Line);
      this.lineListElements$ = of(this.lineArr);
      this.buildSeriesColorMap(this.lineArr);
    });
  }

  getSeverityTrendData(): void {
    const lineSelect = this.linesSelectedArr?.lines?.length
      ? this.linesSelectedArr.lines.toString()
      : null;

    this.loadingService.show();

    this.trendReportService
      .trendDateRange(this.plant, this.fromDateString, this.toDateString, this.groupBy, lineSelect)
      .subscribe({
        next: (response) => {
          this.trendChartData = response.Body;
          this.shiftDateLabels = [];
          this.trendChartDataSource = [];

          if (this.trendChartData?.length > 0) {
            this.shiftDateLabels = this.trendChartData
              .map((obj: any) => obj.ShiftDate.substring(5, 10))
              .filter((elem: string, index: number, self: string[]) => index === self.indexOf(elem));

            const linesToRender = this.linesSelectedArr?.lines?.length
              ? this.linesSelectedArr.lines
              : this.lineArr;

            this.buildSeriesColorMap(this.lineArr.length ? this.lineArr : linesToRender);

            for (const line of linesToRender) {
              const major = this.buildLineSeverityData(line, 'Major');
              const minor = this.buildLineSeverityData(line, 'Minor');
              const critical = this.buildLineSeverityData(line, 'Critical');

              if (major.length) {
                this.trendChartDataSource.push({
                  seriesname: `${line} major`,
                  data: major
                });
              }

              if (minor.length) {
                this.trendChartDataSource.push({
                  seriesname: `${line} minor`,
                  data: minor
                });
              }

              if (critical.length) {
                this.trendChartDataSource.push({
                  seriesname: `${line} critical`,
                  data: critical
                });
              }
            }

            this.maxVal =
              Math.max(
                ...this.trendChartData.map((o: any) =>
                  Math.max(
                    Number(o.Minor) || 0,
                    Number(o.Major) || 0,
                    Number(o.Critical) || 0
                  )
                )
              ) + 1;

            if (!isFinite(this.maxVal) || this.maxVal <= 0) {
              this.maxVal = 1;
            }

            this.buildChartOption();
            this.showChart = true;
          } else {
            this.showChart = false;
            this.shiftDateLabels = [];
            this.maxVal = 1;
            this.trendChartDataSource = [];
            this.buildEmptyChartOption();
          }

          this.lineListElements$ = of(this.lineArr);
        },
        error: () => {
          this.showChart = false;
          this.buildEmptyChartOption();
        },
        complete: () => {
          this.loadingService.hide();
        }
      });
  }

  changeLinesDisplayed(): void {
    this.trendChartDataSource = [];
    this.linesSelected = this.linesSelectedArr?.lines ?? [];
    this.getSeverityTrendData();
  }

  onDateChange(e: any): void {
    this.fromDateString = e.fromDate;
    this.toDateString = e.toDate;
    this.currentShift = e.currentShift;
    this.getSeverityTrendData();
  }

  private buildLineSeverityData(
    line: string,
    severityKey: 'Major' | 'Minor' | 'Critical'
  ): { label: string; value: number }[] {
    return this.trendChartData
      .filter((obj: any) => obj.Line === line)
      .map((obj: any) => ({
        label: obj.ShiftDate.substring(5, 10),
        value: Number(obj[severityKey]) || 0
      }));
  }

  private buildSeriesColorMap(lines: string[]): void {
    const map: Record<string, string> = {};
    let colorIndex = 0;

    for (const line of lines) {
      for (const severity of this.severityOrder) {
        map[`${line} ${severity}`] =
          this.seriesPalette[colorIndex % this.seriesPalette.length];
        colorIndex++;
      }
    }

    this.seriesColorMap = map;
  }

  buildChartOption(): void {
    const shouldRotateDates = this.shiftDateLabels.length >= 30;

    this.chartOption = {
      title: {
        text: `${this.plant} EOLA Findings By Category`,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 14,
          fontWeight: '600',
          color: '#666'
        }
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#444'
        },
        formatter: (params: any[]) => this.getTooltipFormatter(params)
      },
      legend: {
        type: 'scroll',
        orient: 'vertical',
        right: 10,
        top: 'middle',
        bottom: 20,
        itemWidth: 14,
        itemHeight: 14,
        textStyle: {
          color: '#666',
          fontSize: 12
        },
        data: this.trendChartDataSource.map(series => series.seriesname)
      },
      grid: {left: 55, right: 120, top: 60, bottom: shouldRotateDates ? 65 : 60, containLabel: false},
      xAxis: {
        type: 'category',
        name: 'Date',
        nameLocation: 'middle',
        nameGap: 35,
        data: this.shiftDateLabels,
        boundaryGap: false,
        nameTextStyle: {
          color: '#666',
          fontSize: 14
        },
        axisLine: {
          lineStyle: { color: '#ccc' }
        },
        axisTick: { show: false },
        axisLabel: {
          rotate: shouldRotateDates ? 45 : 0,
          interval: 0,
          hideOverlap: true,
          color: '#666',
          fontSize: 11
        },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        name: 'EOLA Finding By Catergory',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 25,
        min: 0,
        max: this.maxVal,
        nameTextStyle: { color: '#666', fontSize: 14 },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#666', fontSize: 12,
          formatter: (value: number) => this.formatYAxisLabel(value)
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#e9edf5', type: 'solid' }
        }
      },
      series: this.trendChartDataSource.map(series =>
        this.createSeries(series.seriesname, series.data)
      )
    };
  }

  buildEmptyChartOption(): void {
    this.chartOption = {
      title: {
        text: `${this.plant} EOLA Findings By Category`,
        left: 'center',
        top: 10,
        textStyle: { fontSize: 14, fontWeight: '600', color: '#666' }
      },
      legend: {
        type: 'scroll',
        orient: 'vertical',
        right: 10,
        top: 'middle',
        data: []
      },
      grid: { left: 55, right: 170, top: 60, bottom: 70 },
      xAxis: {
        type: 'category',
        name: 'Date',
        nameLocation: 'middle',
        nameGap: 35,
        data: []
      },
      yAxis: {
        type: 'value',
        name: 'EOLA Finding By Catergory',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 35,
        min: 0,
        max: 1
      },
      series: []
    };
  }

  private createSeries(
    name: string,
    dataArr: { label: string; value: number }[]
  ): any {
    const color = this.getSeriesColor(name);
    const dataMap = new Map(dataArr.map(item => [item.label, item.value]));

    return {
      name,
      type: 'line',
      smooth: false,
      connectNulls: true,
      symbol: 'circle',
      symbolSize: 6,
      showSymbol: true,
      lineStyle: {
        width: 1,
        color
      },
      itemStyle: {
        color: '#ffffff',
        borderColor: color,
        borderWidth: 1
      },
      data: this.shiftDateLabels.map(label => dataMap.get(label) ?? null)
    };
  }

  private getSeriesColor(seriesName: string): string {
    return this.seriesColorMap[seriesName] || '#999';
  }

  private formatYAxisLabel(value: number): string {
    if (value === 0) {
      return '0';
    }

    if (value >= 1000) {
      const k = value / 1000;
      return Number.isInteger(k) ? `${k}K` : `${k.toFixed(2)}K`;
    }

    return `${value}`;
  }

  private getTooltipFormatter(params: any[]): string {
    if (!params || !params.length) {
      return '';
    }

    const dateLabel = params[0].axisValueLabel || params[0].axisValue;

    const validParams = params.filter(
      (p: any) =>
        p.data !== null &&
        p.data !== undefined &&
        p.data !== '' &&
        p.data !== '-'
    );

    if (!validParams.length) {
      return `<div>${dateLabel}</div>`;
    }

    const rows = validParams
      .map((p: any) => {
        const color = this.getSeriesColor(p.seriesName);
        const value = Number(p.data).toLocaleString();

        const marker = `
          <span style="
            display:inline-block;
            width:10px;
            height:10px;
            margin-right:6px;
            border:2px solid ${color};
            border-radius:50%;
            background:#fff;
            vertical-align:middle;
            box-sizing:border-box;
          "></span>
        `;

        return `
          <div style="display:flex; justify-content:space-between; gap:16px; align-items:center;">
            <span>${marker}${p.seriesName}</span>
            <span><b>${value}</b></span>
          </div>
        `;
      })
      .join('');

    return `
      <div style="min-width:190px;">
        <div style="margin-bottom:6px;">${dateLabel}</div>
        ${rows}
      </div>
    `;
  }
}